import 'react-toastify/dist/ReactToastify.min.css';

import Image from 'next/image';
import type { ToastOptions } from 'react-toastify';
import { cssTransition, Flip, toast } from 'react-toastify';

import { P } from '~components/Typography';
import { ButtonInline } from '~shared/ui/molecules';

import {
	BurstAmount,
	BurstInner,
	BurstOuter,
	BurstWrapper,
	Flame,
	Flex,
	Jumbo,
	Level,
	Neuron,
	Wrapper,
} from './Toaster.styles';

const BagelTransition = cssTransition({
	enter: 'bagel-enter',
	exit: 'bagel-exit',
	collapseDuration: 1000,
});

const BurstTransition = cssTransition({
	enter: 'burst-enter',
	exit: 'burst-exit',
	collapseDuration: 1000,
});

const baseConfig = {
	containerId: 'main',
	draggable: true,
};

const bagelConfig: ToastOptions = {
	icon: false,
	draggable: false,
	className: 'bagel',
	autoClose: 3000,
	closeButton: false,
	closeOnClick: true,
	transition: BagelTransition,
};

type TToast = {
	title: string;
	text?: string;
	onUndo?: () => void;
};

const Toast = ({ title, text, onUndo }: TToast) => (
	<Wrapper>
		{title && (
			<Flex>
				<P size="medium" align="left">
					{title}
				</P>
				{typeof onUndo === 'function' && (
					<ButtonInline onClick={onUndo} text="Undo" underline />
				)}
			</Flex>
		)}
		{text && (
			<P size="medium" align="left" transparent={true}>
				{text}
			</P>
		)}
	</Wrapper>
);

const ToastMessages = ({ text }: { text: string }) => (
	<Wrapper>{text && <P size="medium">{text}</P>}</Wrapper>
);

const LevelToast = ({ level }: { level: number }) => (
	<>
		<Level
			data-level={level}
			className="BagelToast__level"
			data-smalltext={level > 99}
		/>
		<Wrapper>
			<P align="left">Congratulations</P>
			<P size="small" align="left" transparent={true}>
				You&apos;ve reached level {level}
			</P>
		</Wrapper>
	</>
);

const NeuronsToast = ({ amount }: { amount: string }) => (
	<>
		<Neuron className="BagelToast__neuron">
			<Image
				width={52}
				height={52}
				src="/img/neurons/gem-icon.png"
				alt="neuron image"
				quality={100}
			/>
		</Neuron>
		<Wrapper>
			<P align="left">Congratulations</P>
			<P size="small" align="left" transparent={true}>
				{amount} neurons added to your account
			</P>
		</Wrapper>
	</>
);

export const BurstToast = ({ amount }: { amount: string }) => (
	<BurstWrapper>
		<Jumbo className="BurstToast__jumbo">
			<Image
				src="/img/neurons/gem-icon.png"
				width={130}
				height={130}
				quality={100}
				alt="neuron image"
			/>
		</Jumbo>
		<BurstOuter className="BurstToast__outer">
			<BurstInner className="BurstToast__inner">
				<BurstAmount>
					+{amount}
					<span>neurons</span>
				</BurstAmount>
			</BurstInner>
		</BurstOuter>
	</BurstWrapper>
);

export const DemoGenerationToast = ({ amount }: { amount: string }) => (
	<>
		<Flame>
			<Image
				src="/img/gtool/flame.gif"
				width={40}
				height={40}
				quality={85}
				alt="flame image"
			/>
		</Flame>
		<Wrapper>
			<P align="left">Congratulations!</P>
			<P size="small" align="left" transparent={true}>
				You have {amount} free generations
			</P>
		</Wrapper>
	</>
);

export const Toaster = {
	show: ({ title = '', text = '', ...cfg }) =>
		toast.success(<Toast {...{ title, text }} />, { ...baseConfig, ...cfg }),
	showSuccess: ({ title = '', text = '', ...cfg }) =>
		toast.success(<Toast {...{ title, text }} />, {
			icon: () => (
				<Image src="/assets/icons/tick.svg" alt="tick" width={24} height={24} />
			),
			...baseConfig,
			...cfg,
		}),
	showInfo: ({ title = '', text = '' }) =>
		toast.info(<Toast {...{ title, text }} />, baseConfig),
	showWarn: ({ title = '', text = '' }) =>
		toast.warn(<Toast {...{ title, text }} />, baseConfig),
	showError: ({ title = '', text = '' }) =>
		toast.error(<Toast {...{ title, text }} />, baseConfig),
	showLevelMessage: ({ text = '' }) =>
		toast.info(<ToastMessages {...{ text }} />, {
			containerId: 'messages-toast',
			transition: Flip,
			className: 'baguette',
			closeButton: false,
			position: 'top-center',
			icon: false,
			draggable: false,
			delay: 4000,
			autoClose: 5000,
		}),
	showWithoutIcon: ({ title = '', text = '' }) =>
		toast.success(<Toast {...{ title, text }} />, {
			icon: () => null,
			...baseConfig,
		}),
	showWithUndo: ({
		title = '',
		onClose,
		undo,
	}: {
		title: string;
		onClose: () => void;
		undo: () => void;
	}) => {
		let isUndone = false;

		const handleUndo = () => {
			isUndone = true;
			undo();
		};

		toast.error(<Toast {...{ title, onUndo: handleUndo }} />, {
			...baseConfig,
			autoClose: 4000,
			closeButton: false,
			closeOnClick: true,
			hideProgressBar: false,
			onClose: () => {
				if (!isUndone) {
					onClose();
				}
			},
		});
	},
	showLevel: ({ level }: { level: number }) =>
		toast.success(<LevelToast {...{ level }} />, {
			...baseConfig,
			...bagelConfig,
		}),
	showNeurons: ({ amount }: { amount: string }) =>
		toast.success(<NeuronsToast {...{ amount }} />, {
			...baseConfig,
			...bagelConfig,
			delay: 300,
		}),
	showImageGeneration: ({ amount }: { amount: string }) =>
		toast.success(<DemoGenerationToast {...{ amount }} />, {
			...baseConfig,
			...bagelConfig,
			delay: 300,
			autoClose: 10000,
		}),
	showNeuronsBurst: ({ amount }: { amount: string }) =>
		toast.success(<BurstToast {...{ amount }} />, {
			...baseConfig,
			containerId: 'center-toast',
			icon: false,
			draggable: false,
			autoClose: 100,
			closeButton: false,
			closeOnClick: true,
			className: 'burst',
			position: 'top-center',
			transition: BurstTransition,
		}),
};
